import { KeyboardEvent } from 'react';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseConfirmDrawer from '../../ui/BaseConfirmDrawer';
import useAddApHook, { AP_INPUT_CONFIG } from '@/hooks/registration/useAddApHook';
import BaseTextField from '@/components/form/BaseTextField';
import { APDialogState, ValidatedMessage } from '@/types/pages/storeManagement/registration';
import { INPUT_CATEGORY } from '@/types/pages/storeManagement/registration/registrationTypes';
import { usePdaScannerInput } from '@/mobile/features/register-device/hooks/usePdaScannerInput';

type ApInputConfigItem = (typeof AP_INPUT_CONFIG)[number];

const isApMacInput = (item: ApInputConfigItem) => {
    return item.category === INPUT_CATEGORY.AP_MAC;
};

const getApInputSx = (hasError: boolean) => ({
    '& .MuiOutlinedInput-root': {
        height: 38,
        borderRadius: 1.5,
        backgroundColor: '#FFFFFF',
        fontSize: 15,

        '& fieldset': {
            borderColor: hasError ? '#DC2626' : '#D1D5DB',
        },

        '&:hover fieldset': {
            borderColor: hasError ? '#DC2626' : '#94A3B8',
        },

        '&.Mui-focused fieldset': {
            borderColor: hasError ? '#DC2626' : '#0B5BD3',
            borderWidth: 1,
        },
    },

    '& .MuiOutlinedInput-input': {
        px: 1.5,
        py: 0,
        color: '#111827',
    },

    '& .MuiOutlinedInput-input::placeholder': {
        color: '#6B7280',
        opacity: 1,
    },
});

const AddApDrawer = () => {
    const { t } = useTranslation('common');
    const {
        state,
        apState,
        inputRefs,
        hasEditData,
        confirmDisabled,
        closeApDialog,
        resetApDialog,
        handleChange,
        normalizeEncodedMacValue,
        handleValidate,
        isInputDisabled,
        submitApForm,
        handleApMacScan,
    } = useAddApHook();

    const apMacScannerInputHandlers = usePdaScannerInput({
        enabled: state.dialogState.isApOpen,
        disabled: isInputDisabled(INPUT_CATEGORY.AP_MAC),
        transform: (value) => value.trim(),
        onScan: handleApMacScan,
    });

    const getApMacScannerInputHandlers = (item: ApInputConfigItem) => {
        if (!isApMacInput(item)) {
            return {};
        }

        return apMacScannerInputHandlers;
    };

    const handleApMacEnterKey = (event: KeyboardEvent, item: ApInputConfigItem) => {
        if (!isApMacInput(item)) return;
        if (event.key !== 'Enter') return;
        const inputValue = (event.target as HTMLInputElement).value.trim();

        if (!inputValue.startsWith('++')) return;

        event.preventDefault();
        event.stopPropagation();

        normalizeEncodedMacValue(inputValue, item.category);
    };

    const handleInputKeyUp = (event: KeyboardEvent, item: ApInputConfigItem) => {
        if (isApMacInput(item) && event.key === 'Enter') return;

        handleValidate(event, item.category);
    };

    return (
        <BaseConfirmDrawer
            open={state.dialogState.isApOpen}
            title={hasEditData ? t('Edit AP') : t('Add AP')}
            confirmText={t('Save')}
            cancelText={t('Cancel')}
            confirmDisabled={confirmDisabled}
            showCloseButton={false}
            onClose={closeApDialog}
            onConfirm={submitApForm}
            onExited={resetApDialog}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    {AP_INPUT_CONFIG.map((item) => {
                        const value = apState[item.key as keyof APDialogState];
                        const error = apState.validatedMessage[item.key as keyof ValidatedMessage];
                        const hasError = error !== '';

                        return (
                            <FormControl required key={item.id} error={hasError}>
                                <Typography
                                    component="label"
                                    sx={{
                                        mb: 0.5,
                                        fontSize: 16,
                                        fontWeight: 800,
                                        lineHeight: 1.3,
                                        color: '#111827',
                                    }}
                                >
                                    {t(item.label)}
                                </Typography>

                                <BaseTextField
                                    {...getApMacScannerInputHandlers(item)}
                                    inputRef={(element) => {
                                        inputRefs.current[item.key] = element;
                                    }}
                                    value={item.format(value)}
                                    type={item.type}
                                    required
                                    placeholder={item.placeholder}
                                    slotProps={item.slotProps}
                                    error={hasError}
                                    disabled={isInputDisabled(item.category)}
                                    onChange={(event) => handleChange(event, item.category)}
                                    onKeyDown={(event) => handleApMacEnterKey(event, item)}
                                    onKeyUp={(event) => handleInputKeyUp(event, item)}
                                    sx={getApInputSx(hasError)}
                                />

                                <FormHelperText
                                    sx={{
                                        mx: 0,
                                        mt: 0.5,
                                        minHeight: 18,
                                        fontSize: 12,
                                    }}
                                >
                                    {error}
                                </FormHelperText>
                            </FormControl>
                        );
                    })}
                </Box>
            </Box>
        </BaseConfirmDrawer>
    );
};

export default AddApDrawer;
